import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import ggbooklet from '@/assets/ggbooklet.webp';
import phoneguide from '@/assets/phoneguide.png';
import computerguide from '@/assets/computerguide.png';
import usermanual from '@/assets/usermanual.png';
import {
  createUpdateSubscription,
  checkDiscountCode,
  type DiscountCodeDetails,
} from '@/hooks/useStripe';
import { getCookie } from '@/lib/cookies';
import { getCurrencySymbol, type CurrencyCode } from '@/lib/currency';
import { calculateFamilyPrice } from '@/lib/familyUpgradePricing';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { ArrowLeft, ArrowRight, Check, Heart, Minus, Package, Plus, ShieldCheck, Tag, User } from 'lucide-react';

const KIT_PRICE = 4.99;
const MARKETING_BASE_URL = 'https://granguard.org';

// Shared with /subscribe so a code carried in from a campaign (e.g. the quiz funnel)
// survives the sign-in / sign-up round trip whichever page it started on.
const PENDING_DISCOUNT_KEY = 'gg_pending_discount';

function readStoredDiscount(): string {
  if (typeof window === 'undefined') return '';
  try {
    return (window.localStorage.getItem(PENDING_DISCOUNT_KEY) || '').trim();
  } catch {
    return '';
  }
}

function storeDiscount(code: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_DISCOUNT_KEY, code.trim().toUpperCase());
  } catch {
    /* storage unavailable (private mode, etc.) */
  }
}

function clearStoredDiscount() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_DISCOUNT_KEY);
  } catch {
    /* storage unavailable */
  }
}

type Plan = 'individual' | 'family';
type Slide = 'plan' | 'family' | 'checkout';

export const Route = createFileRoute('/subscribe-2')({
  validateSearch: (search: Record<string, unknown>) => ({
    // individual | family; legacy personal/sponsor values map across.
    plan: typeof search.plan === 'string' ? search.plan : undefined,
    me: typeof search.me === 'string' ? search.me : undefined,
    billing: typeof search.billing === 'string' ? search.billing : undefined,
    discount: typeof search.discount === 'string' ? search.discount : undefined,
    seats: typeof search.seats === 'string' ? search.seats : typeof search.seats === 'number' ? String(search.seats) : undefined,
    kit: typeof search.kit === 'string' ? search.kit : undefined,
    // 'stack' = plan cards vertically; anything else = side-by-side.
    layout: typeof search.layout === 'string' ? search.layout : undefined,
  }),
  component: SubscribeV2,
});

function SubscribeV2() {
  const search = Route.useSearch();
  const { user: clerkUser, isLoaded: clerkLoaded } = useClerkUser();
  const currencyCookie = (typeof document !== 'undefined' ? getCookie('gg_currency') : null) as CurrencyCode | null;
  const currencySymbol = getCurrencySymbol(currencyCookie ?? 'USD');

  const initialPlan: Plan | null =
    search.plan === 'family' || search.plan === 'sponsor'
      ? 'family'
      : search.plan === 'individual' || search.plan === 'personal'
        ? 'individual'
        : null;
  const initialBilling: 'month' | 'year' = search.billing === 'annual' || search.billing === 'year' ? 'year' : 'month';
  const seatsFromUrl = Math.floor(Number(search.seats));
  const initialPeople =
    Number.isFinite(seatsFromUrl) && seatsFromUrl >= 1 ? seatsFromUrl : initialPlan === 'family' ? 2 : 1;
  // Someone returning from the auth round trip already made their choices.
  const cameConfigured = !!initialPlan;

  const [plan, setPlan] = useState<Plan>(initialPlan ?? 'individual');
  const [people, setPeople] = useState(initialPeople);
  const [includeMe, setIncludeMe] = useState(search.me !== '0');
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>(initialBilling);
  const [hasKit, setHasKit] = useState(search.kit === '1');
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCodeDetails | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [isKitInfoOpen, setIsKitInfoOpen] = useState(false);

  // Wizard slides with directional transitions.
  const [slide, setSlide] = useState<Slide>(cameConfigured ? 'checkout' : 'plan');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const goto = (next: Slide, dir: 'forward' | 'back') => {
    setDirection(dir);
    setSlide(next);
  };

  const signedIn = !!clerkUser;
  const isFamily = plan === 'family';
  const protectedPeople = isFamily ? people : 1;
  const stackedPlans = search.layout === 'stack';

  const choosePlan = (p: Plan) => {
    setPlan(p);
    if (p === 'individual') {
      setPeople(1);
      goto('checkout', 'forward');
    } else {
      setPeople((n) => Math.max(n, 2));
      goto('family', 'forward');
    }
  };

  // ---- Pricing: by protected-people count only (matches the API's seat pricing) ----
  const recurringBeforeDiscount = calculateFamilyPrice(protectedPeople, billingPeriod === 'year');
  const recurringBeforeCents = Math.round(recurringBeforeDiscount * 100);
  const discountCents = (() => {
    if (!appliedDiscount) return 0;
    if (!appliedDiscount.active || !appliedDiscount.valid) return 0;
    if (appliedDiscount.minimumAmount && recurringBeforeCents < appliedDiscount.minimumAmount) return 0;
    if (appliedDiscount.amountOff && appliedDiscount.amountOff > 0) {
      return Math.min(appliedDiscount.amountOff, recurringBeforeCents);
    }
    if (appliedDiscount.percentOff && appliedDiscount.percentOff > 0) {
      return Math.floor((recurringBeforeCents * appliedDiscount.percentOff) / 100);
    }
    return 0;
  })();
  const recurringAfterDiscount = Math.max(0, (recurringBeforeCents - discountCents) / 100);
  const totalKit = hasKit ? KIT_PRICE : 0;
  const totalToday = recurringAfterDiscount + totalKit;
  const periodLabel = billingPeriod === 'year' ? 'year' : 'month';
  const monthlyEquivalent = billingPeriod === 'year' ? recurringAfterDiscount / 12 : recurringAfterDiscount;
  const formatPrice = (amount: number) => `${currencySymbol}${amount.toFixed(2)}`;

  const soloMonthly = calculateFamilyPrice(1, false);
  const coupleMonthly = calculateFamilyPrice(2, false);
  const familyMonthlyNow = calculateFamilyPrice(protectedPeople, false);

  const updateSubscription = createUpdateSubscription();
  const checkCodeMutation = checkDiscountCode();
  const didAutoApply = useRef(false);

  const pendingCode = (search.discount || readStoredDiscount() || '').trim().toUpperCase();
  const validatingDiscount = checkCodeMutation.status === 'pending' && !appliedDiscount && !discountError;

  // Stash a URL discount the moment we see it, so it survives the auth round trip.
  useEffect(() => {
    if (search.discount && search.discount.trim()) {
      storeDiscount(search.discount.trim());
    }
  }, [search.discount]);

  // Auto-apply discount code from URL or storage once signed in (validation needs auth).
  useEffect(() => {
    if (!clerkUser) return;
    if (typeof window === 'undefined') return;
    if (didAutoApply.current) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (search.discount || params.get('discount') || readStoredDiscount() || '').trim();
    if (fromUrl) {
      didAutoApply.current = true;
      setCouponCode(fromUrl.toUpperCase());
      const applyCode = async () => {
        try {
          const details = await checkCodeMutation.mutateAsync(fromUrl.trim().toUpperCase());
          const isExpired = details.expiresAt !== null && (details.expiresAt * 1000) <= Date.now();
          if (isExpired || !details.active || !details.valid) {
            setDiscountError(isExpired ? 'Expired discount code' : 'Invalid discount code');
            return;
          }
          setAppliedDiscount(details);
          setCouponCode((details.code || fromUrl).toUpperCase());
        } catch {
          setDiscountError('Invalid discount code');
        }
      };
      applyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkUser]);

  if (!clerkLoaded) {
    return <div className="min-h-screen bg-white" />;
  }

  const configSearch = () => {
    const params = new URLSearchParams();
    params.set('plan', plan);
    params.set('billing', billingPeriod);
    params.set('seats', String(protectedPeople));
    params.set('me', isFamily && !includeMe ? '0' : '1');
    if (hasKit) params.set('kit', '1');
    const code = (appliedDiscount?.code || couponCode || pendingCode || '').trim();
    if (code) params.set('discount', code.toUpperCase());
    if (search.layout) params.set('layout', search.layout);
    return `?${params.toString()}`;
  };

  const handleContinue = async () => {
    setError('');

    // Signed-out visitors configure first, then create the account. Their choices ride
    // along on the redirect URL (plus localStorage for the code) and are restored here.
    if (!clerkUser) {
      const code = (couponCode || pendingCode || '').trim();
      if (code) storeDiscount(code);
      window.location.assign(`/signup?redirectTo=${encodeURIComponent('/subscribe-2' + configSearch())}`);
      return;
    }

    // Seats: the buyer takes one unless they are only helping; the rest are invite seats.
    const userLicenses: Record<string, 'FREE' | 'PAID' | number> = {};
    if (isFamily && !includeMe) {
      userLicenses.UNASSIGNED = protectedPeople;
    } else {
      userLicenses[clerkUser.id] = 'PAID';
      if (protectedPeople > 1) userLicenses.UNASSIGNED = protectedPeople - 1;
    }
    const welcomePack = hasKit ? { type: 'both', sent: false } : undefined as any;
    try {
      const codeToSend = appliedDiscount && appliedDiscount.active && appliedDiscount.valid ? (appliedDiscount.code || '').toUpperCase() : undefined;
      const result = await updateSubscription.mutateAsync({ userLicenses, welcomePack, couponCode: codeToSend, returnTo: '/subscribe-2', interval: billingPeriod });
      clearStoredDiscount();
      if (result && typeof result === 'object' && 'redirectUrl' in result && typeof result.redirectUrl === 'string') {
        window.location.assign(result.redirectUrl);
      } else if (result && 'success' in result && result.success) {
        window.location.assign('/');
      }
    } catch (err: any) {
      const serverMsg = err?.response?.data?.message;
      setError(serverMsg || err?.message || 'Failed to start subscription. Please try again.');
    }
  };

  const handleApplyCoupon = async () => {
    setDiscountError('');
    setAppliedDiscount(null);
    const code = (couponCode ?? '').trim().toUpperCase();
    if (!code) {
      setDiscountError('Enter a code to apply.');
      return;
    }
    // Signed out we cannot validate yet; save it and apply after account creation.
    if (!clerkUser) {
      storeDiscount(code);
      setCouponCode(code);
      return;
    }
    try {
      setIsApplyingDiscount(true);
      const details = await checkCodeMutation.mutateAsync(code);
      const isExpired = details.expiresAt !== null && (details.expiresAt * 1000) <= Date.now();
      if (isExpired) {
        setDiscountError('Expired discount code');
        return;
      }
      if (!details.active || !details.valid) {
        setDiscountError('Invalid discount code');
        return;
      }
      setAppliedDiscount(details);
      setCouponCode((details.code || code).toUpperCase());
    } catch (err: any) {
      setDiscountError(err?.response?.data?.error || 'Invalid discount code');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // "for your first year" / "for your first 3 months" / "for your first month" / "for as long as you subscribe"
  const discountDurationCopy = (d: DiscountCodeDetails): string => {
    if (d.duration === 'forever') return 'for as long as you subscribe';
    if (d.duration === 'once') return billingPeriod === 'year' ? 'for your first year' : 'for your first month';
    const months = d.durationInMonths ?? 0;
    if (months <= 0) return '';
    if (months % 12 === 0) {
      const years = months / 12;
      return years === 1 ? 'for your first year' : `for your first ${years} years`;
    }
    return `for your first ${months} months`;
  };

  const savedCodeForLater = !signedIn && !!(pendingCode || couponCode.trim());
  const savedCode = (couponCode.trim() || pendingCode).toUpperCase();

  const summaryLabel = !isFamily
    ? 'Individual, protection for you'
    : includeMe
      ? `Family, you + ${protectedPeople - 1} ${protectedPeople === 2 ? 'other' : 'others'}`
      : `Family, ${protectedPeople} loved ${protectedPeople === 1 ? 'one' : 'ones'} (your account free)`;

  // Path-aware progress: Individual skips the family slide.
  const path: Slide[] = isFamily || slide === 'family' ? ['plan', 'family', 'checkout'] : ['plan', 'checkout'];
  const slideIndex = Math.max(0, path.indexOf(slide));

  const slideAnim = direction === 'forward' ? 'gg-slide-forward' : 'gg-slide-back';

  return (
    <div className="min-h-screen bg-white flex items-start sm:items-center justify-center px-0 py-0 sm:px-6 sm:py-8">
      <style>{`
        @keyframes gg-in-right { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: none; } }
        @keyframes gg-in-left { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: none; } }
        .gg-slide-forward { animation: gg-in-right 0.28s ease; }
        .gg-slide-back { animation: gg-in-left 0.28s ease; }
        @media (prefers-reduced-motion: reduce) {
          .gg-slide-forward, .gg-slide-back { animation: none; }
        }
      `}</style>
      <div className="w-full max-w-full sm:max-w-4xl">
        <div className="overflow-hidden p-5 sm:p-8">
          {/* Header: back arrow, dots, title */}
          <div className="relative flex items-center justify-center pb-1">
            {slide !== 'plan' && (
              <button
                type="button"
                onClick={() => goto(slide === 'checkout' && isFamily ? 'family' : 'plan', 'back')}
                className="absolute left-0 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-center gap-2" aria-label={`Step ${slideIndex + 1} of ${path.length}`}>
              {path.map((s, i) => (
                <span
                  key={s}
                  className={`h-2 rounded-full transition-all ${i === slideIndex ? 'w-6 bg-violet-600' : 'w-2 bg-slate-200'}`}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>

          {validatingDiscount ? (
            <Banner tone="violet">Applying your discount...</Banner>
          ) : appliedDiscount && (appliedDiscount.percentOff || appliedDiscount.amountOff) ? (
            <Banner tone="green">
              Code <span className="font-semibold">{(appliedDiscount.code || couponCode).toUpperCase()}</span> applied:{' '}
              {formatPrice(recurringAfterDiscount)}/{periodLabel} {discountDurationCopy(appliedDiscount)}.
            </Banner>
          ) : savedCodeForLater ? (
            <Banner tone="green">
              Code <span className="font-semibold">{savedCode}</span> saved. We'll apply it before you pay.
            </Banner>
          ) : pendingCode && discountError ? (
            <Banner tone="amber">We couldn't apply {pendingCode}. You can continue without it.</Banner>
          ) : null}

          {/* ---- Slide 1: plan selection (homepage pricing card language) ---- */}
          {slide === 'plan' && (
            <div key="plan" className={`${slideAnim} pt-3 space-y-9`}>
              <div className="text-center">
                <h1 className="text-2xl sm:text-[1.7rem] font-semibold text-slate-900">Choose your plan</h1>
                <p className="mt-1 text-base text-slate-500">Both include every GranGuard feature. Cancel anytime.</p>
              </div>

              <div className={stackedPlans ? 'mx-auto grid max-w-xl gap-4' : 'grid gap-4 sm:grid-cols-2 sm:items-stretch'}>
                <PlanCard
                  name="Individual"
                  tagline="For protecting yourself"
                  description="Full access for one person. All training, tools, and support included."
                  priceMain={formatPrice(soloMonthly)}
                  priceNote="/mo"
                  featureHeading="Individual plan includes:"
                  features={[
                    'Interactive scam-defense training',
                    'Scam checker for links, texts, and emails',
                    'Data breach monitoring',
                    'Human support when something feels off',
                  ]}
                  cta="Protect yourself"
                  onSelect={() => choosePlan('individual')}
                />
                <PlanCard
                  popular
                  name="Family Plan"
                  tagline="For helping the people you care about"
                  description={
                    <>
                      <strong className="font-semibold text-slate-700">3 accounts included:</strong> your free helper
                      account, plus full protection for two loved ones.
                    </>
                  }
                  priceMain={formatPrice(coupleMonthly)}
                  priceNote="/mo"
                  featureHeading="Everything in the Individual plan, plus"
                  features={[
                    '3 accounts: yours + two loved ones',
                    'Your helper account included free',
                    'Invite a parent, spouse, or family member',
                    'See progress and completed lessons',
                    'Get guidance on supporting without taking over',
                  ]}
                  cta="Protect your loved ones"
                  onSelect={() => choosePlan('family')}
                />
              </div>
            </div>
          )}

          {/* ---- Slide 2 (family only): who's protected ---- */}
          {slide === 'family' && (
            <div key="family" className={`${slideAnim} mx-auto max-w-xl pt-3 space-y-4`}>
              <div className="text-center">
                <h1 className="text-2xl sm:text-[1.7rem] font-semibold text-slate-900">Who's protected?</h1>
                <p className="mt-1 text-base text-slate-500">You can change this or add more people later.</p>
              </div>

              <div className="rounded-2xl bg-violet-50/60 p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-900">
                    {protectedPeople} {protectedPeople === 1 ? 'person' : 'people'}
                  </p>
                  <p className="text-base text-slate-600">{formatPrice(familyMonthlyNow)}/month</p>
                </div>
                <Stepper value={people} min={1} onChange={setPeople} />
              </div>

              <div className="space-y-2" role="radiogroup" aria-label="Are you one of them?">
                <p className="text-base font-semibold text-slate-900">Are you one of them?</p>
                <ChoiceRow
                  icon={<User className="h-6 w-6" />}
                  title="Yes, include me"
                  sub="One of the accounts is yours"
                  onSelect={() => {
                    setIncludeMe(true);
                    goto('checkout', 'forward');
                  }}
                />
                <ChoiceRow
                  icon={<Heart className="h-6 w-6" />}
                  title="No, I'm helping someone else"
                  sub="You get a free helper account"
                  onSelect={() => {
                    setIncludeMe(false);
                    goto('checkout', 'forward');
                  }}
                />
              </div>
            </div>
          )}

          {/* ---- Slide 3: review and pay ---- */}
          {slide === 'checkout' && (
            <div key="checkout" className={`${slideAnim} mx-auto max-w-xl pt-3 space-y-4`}>
              <div className="text-center">
                <h1 className="text-2xl sm:text-[1.7rem] font-semibold text-slate-900">Review and pay</h1>
                <p className="mt-1 text-base text-slate-500">{summaryLabel}</p>
              </div>

              <div className="divide-y divide-slate-200 border-y border-slate-200">
                <div className="py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <div>
                    <p className="text-base font-semibold text-slate-900">Billing</p>
                    {billingPeriod === 'year' && (
                      <p className="text-base text-emerald-700">{formatPrice(monthlyEquivalent)}/month, billed once a year</p>
                    )}
                  </div>
                  <div className="inline-flex rounded-full bg-slate-100 p-1" role="group" aria-label="Billing period">
                    <button
                      type="button"
                      aria-pressed={billingPeriod === 'month'}
                      onClick={() => setBillingPeriod('month')}
                      className={`h-11 cursor-pointer rounded-full px-4 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
                        billingPeriod === 'month' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      aria-pressed={billingPeriod === 'year'}
                      onClick={() => setBillingPeriod('year')}
                      className={`h-11 cursor-pointer rounded-full px-4 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
                        billingPeriod === 'year' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Annual, save 20%
                    </button>
                  </div>
                </div>

                <div className="py-3">
                  <div className="flex items-center justify-between gap-4">
                    <label className="flex min-w-0 cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={hasKit}
                        onChange={(e) => setHasKit(e.target.checked)}
                        className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 accent-violet-600"
                      />
                      <span className="min-w-0">
                        <span className="block text-base font-medium text-slate-800">
                          Printed Starter Kit, {formatPrice(KIT_PRICE)} one time
                        </span>
                        <span className="block text-base text-slate-500">
                          Everything you need to get started.{' '}
                          <button
                            type="button"
                            className="relative cursor-pointer text-violet-700 underline underline-offset-2 hover:text-violet-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded"
                            aria-expanded={isKitInfoOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              setIsKitInfoOpen((v) => !v);
                            }}
                          >
                            What's inside?
                          </button>
                        </span>
                      </span>
                    </label>
                    <Package className="h-8 w-8 shrink-0 text-violet-300" aria-hidden="true" />
                  </div>
                  {isKitInfoOpen && (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-base text-slate-700">
                      <ul className="space-y-1.5">
                        <li className="flex items-center gap-2.5">
                          <img src={ggbooklet} alt="" className="h-9 w-7" />
                          Scam Defense handbook
                        </li>
                        <li className="flex items-center gap-2.5">
                          <img src={phoneguide} alt="" className="h-9 w-7" />
                          Fridge + phone quick guides
                        </li>
                        <li className="flex items-center gap-2.5">
                          <img src={computerguide} alt="" className="h-9 w-7" />
                          Computer quick guide
                        </li>
                        <li className="flex items-center gap-2.5">
                          <img src={usermanual} alt="" className="h-9 w-7" />
                          User manual for online tools
                        </li>
                      </ul>
                      <p className="mt-2 text-slate-500">Shipped to your door.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-violet-50/50 p-4 space-y-2.5" aria-live="polite">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-slate-900">Due today</p>
                    <p className="text-base text-slate-500">
                      Then {formatPrice(discountCents > 0 && appliedDiscount?.duration !== 'forever' ? recurringBeforeCents / 100 : recurringAfterDiscount)}/{periodLabel} until you cancel.
                    </p>
                  </div>
                  <div className="text-right">
                    {discountCents > 0 && (
                      <p className="text-base font-normal text-slate-400 line-through">{formatPrice(recurringBeforeCents / 100 + totalKit)}</p>
                    )}
                    <p className="text-3xl font-semibold tracking-tight text-slate-900">{formatPrice(totalToday)}</p>
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setShowPromo((v) => !v)}
                    className="inline-flex cursor-pointer items-center gap-2 text-base font-medium text-violet-700 hover:text-violet-900"
                  >
                    <Tag className="h-4 w-4" />
                    {showPromo ? 'Hide discount code' : 'Add discount code'}
                  </button>

                  {showPromo && (
                    <div className="mt-3">
                      <div className="flex flex-row items-stretch gap-2">
                        <label htmlFor="discount-code-v2" className="sr-only">
                          Discount code
                        </label>
                        <input
                          id="discount-code-v2"
                          type="text"
                          placeholder="Enter discount code"
                          className="h-11 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-violet-500"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          autoComplete="off"
                          disabled={updateSubscription.isPending || checkCodeMutation.status === 'pending'}
                          inputMode="text"
                        />
                        <button
                          type="button"
                          className="h-11 cursor-pointer rounded-xl border border-slate-200 bg-white px-4 text-base font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60"
                          onClick={() => handleApplyCoupon()}
                          disabled={updateSubscription.isPending || isApplyingDiscount}
                        >
                          {isApplyingDiscount ? 'Applying...' : signedIn ? 'Apply code' : 'Save code'}
                        </button>
                      </div>
                      {discountError && <div className="mt-1 text-base text-red-600" role="alert">{discountError}</div>}
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="text-base text-red-600 text-center" role="alert">{error}</div>}

              <div className="space-y-2">
                <button
                  type="button"
                  className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-violet-600 text-white text-base font-semibold shadow-sm hover:bg-violet-700 active:bg-violet-800 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                  onClick={() => handleContinue()}
                  disabled={updateSubscription.isPending}
                >
                  {updateSubscription.isPending
                    ? 'Preparing checkout...'
                    : signedIn
                      ? 'Continue to secure checkout'
                      : 'Continue and create your account'}
                  <ArrowRight className="h-4 w-4" />
                </button>

                <p className="text-center text-[13px] leading-relaxed text-slate-400">
                  Cancel anytime&ensp;·&ensp;30 day money back guarantee&ensp;·&ensp;Secure checkout by Stripe
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline">&ensp;·&ensp;</span>
                  <a href={`${MARKETING_BASE_URL}/policies#terms`} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-slate-600">Terms</a>
                  &ensp;·&ensp;
                  <a href={`${MARKETING_BASE_URL}/policies#privacy`} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-slate-600">Privacy</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({
  name,
  tagline,
  description,
  priceMain,
  priceNote,
  featureHeading,
  features,
  cta,
  popular,
  onSelect,
}: {
  name: string;
  tagline: string;
  description: React.ReactNode;
  priceMain: string;
  priceNote: string;
  featureHeading: string;
  features: string[];
  cta: string;
  popular?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex h-full w-full cursor-pointer flex-col rounded-3xl border bg-white p-6 text-left transition-all duration-300 ease-out hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
        popular
          ? 'border-slate-300 shadow-[0_18px_45px_-34px_rgba(15,23,42,0.45)]'
          : 'border-slate-200 shadow-[0_14px_34px_-30px_rgba(15,23,42,0.4)]'
      }`}
    >
      {popular && (
        <span className="absolute -top-3 left-6 inline-flex items-center rounded-lg bg-[#1f2f50] px-3 py-1 text-[0.8rem] font-semibold text-white shadow-sm">
          <ShieldCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Most popular
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-2xl font-semibold tracking-[-0.01em] text-slate-900">{name}</p>
          <p className="mt-0.5 text-base text-slate-500">{tagline}</p>
        </div>
        <span
          className={`flex h-[4.75rem] w-[4.75rem] shrink-0 flex-col items-center justify-center rounded-full ${
            popular ? 'bg-violet-50 ring-1 ring-violet-100' : 'bg-slate-50 ring-1 ring-slate-100'
          }`}
          aria-hidden="false"
        >
          <span className={`text-lg font-bold leading-none ${popular ? 'text-violet-800' : 'text-slate-800'}`}>{priceMain}</span>
          <span className={`mt-0.5 text-sm ${popular ? 'text-violet-600' : 'text-slate-500'}`}>{priceNote}</span>
        </span>
      </div>
      <p className="mt-3 text-base leading-relaxed text-slate-500">{description}</p>
      <span
        className={`mt-5 block w-full rounded-xl py-3 text-center text-base font-semibold transition-all duration-100 ease-out ${
          popular
            ? 'bg-violet-600 text-white shadow-lg shadow-violet-300/40 hover:bg-violet-500'
            : 'border border-slate-700 bg-white text-slate-800 shadow-lg shadow-slate-200/60 hover:bg-slate-50'
        }`}
      >
        {cta}
      </span>
      <p className="mt-5 text-sm font-semibold text-slate-500">{featureHeading}</p>
      <ul className="mt-3 flex-1 space-y-2.5">
        {features.map((f) => (
          <li key={f} className="flex gap-2 text-base text-slate-600">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50">
              <Check className="h-3 w-3 text-emerald-500" aria-hidden="true" />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}

function ChoiceRow({
  icon,
  title,
  sub,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full cursor-pointer items-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-left transition hover:border-violet-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
    >
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold text-slate-900">{title}</span>
        <span className="block text-base text-slate-600">{sub}</span>
      </span>
      <ArrowRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
    </button>
  );
}

function Banner({ tone, children }: { tone: 'green' | 'violet' | 'amber'; children: React.ReactNode }) {
  const styles = {
    green: 'border-green-200 bg-green-50 text-green-800',
    violet: 'border-violet-200 bg-violet-50 text-violet-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
  }[tone];
  return (
    <div className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-base ${styles}`} role="status">
      <Tag className="h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

type StepperProps = {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
};

function Stepper({ value, min = 1, max, onChange }: StepperProps) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(max ? Math.min(max, value + 1) : value + 1);

  return (
    <div className="inline-flex items-center rounded-xl bg-white p-1 ring-1 ring-slate-200" role="group" aria-label="People protected">
      <button
        type="button"
        onClick={dec}
        className="h-11 w-11 rounded-lg bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        disabled={value <= min}
        aria-label="Fewer people"
      >
        <Minus className="h-4 w-4 mx-auto" />
      </button>

      <div className="px-3.5 text-center" aria-live="polite" aria-atomic="true">
        <p className="text-2xl font-semibold leading-7 text-slate-900">{value}</p>
      </div>

      <button
        type="button"
        onClick={inc}
        className="h-11 w-11 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        disabled={max ? value >= max : false}
        aria-label="More people"
      >
        <Plus className="h-4 w-4 mx-auto" />
      </button>
    </div>
  );
}
